import type { CSSProperties, JSX, ReactNode } from 'react';
import React, { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { getIn } from 'formik';
import type { AnyAction, Dispatch } from 'redux';

import { setErrorFields } from '../../store/fieldError';
import { ErrorFieldCheck } from '../ErrorValidation';
import InputProvider from '../InputProvider';

export function getNestedProperty<T = unknown>(
  obj: Record<string, unknown>,
  path: string
): T | undefined {
  const keys = path.replace(/\[(\d+)\]/g, '.$1').split('.');
  return keys.reduce<unknown>(
    (acc, key) => (acc && typeof acc === 'object' ? (acc as Record<string, unknown>)[key] : undefined),
    obj
  ) as T | undefined;
}
interface Store {
  customer: Record<string, unknown>;
}

interface UnknownRecord {
  [key: string]: unknown;
}

interface FormikLike {
  field: {
    value: unknown;
  };
  form: {
    setFieldValue: (name: string, value: unknown, shouldValidate?: boolean) => void;
    setFieldTouched: (name: string, touched: boolean) => Promise<UnknownRecord>;
    errors: UnknownRecord;
    touched: UnknownRecord;
  };
}

interface InputProviderRenderProps {
  hasError: boolean;
  errorMessage?: string;
  handleOnChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleOnBlur: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

interface FieldInputProps {
  formik: FormikLike;
  store: Store;
  name: string;
  required?: boolean;
  type?: string;
  label?: string;
  footer?: ReactNode;
  placeholder?: string;
  hasGroupError?: boolean;
  dispatch: Dispatch<AnyAction>;
  apiService: {
    updateCustomer: (
      customer: UnknownRecord,
      dispatch: Dispatch<AnyAction>
    ) => void;
  };
  onChange?: (event: unknown) => void;
  customStyles?: CSSProperties;
  disableAutoSave?: boolean;
}


function FieldInput({
  formik,
  store,
  name,
  required,
  type,
  label,
  footer,
  placeholder,
  hasGroupError,
  dispatch,
  apiService,
  onChange,
  customStyles,
  disableAutoSave
}: FieldInputProps): JSX.Element {
  const [isDirty, setFieldDirty] = useState(false);
  const [isTouch, setIsTouch] = useState(false);

  
useEffect(() => {
  const currentValue = getNestedProperty(store.customer, name);
  const formikValue = formik.field.value;

  // Case 1: taxNumber (not taxNumber1)
  if (
    name.includes('taxNumber') &&
    name.split('.').pop() !== 'taxNumber1'
  ) {
    if (currentValue !== undefined && currentValue !== formikValue) {
      formik.form.setFieldValue(name, currentValue);
    }

    if (currentValue === undefined && formikValue !== '') {
      formik.form.setFieldValue(name, '');
    }
  }

  // Case 2: taxNumber1
  if (
    name.split('.').pop() === 'taxNumber1' &&
    currentValue !== undefined &&
    currentValue !== formikValue
  ) {
    formik.form.setFieldTouched(name, true).then(error => {
      if (getIn(error, name) === undefined) {
        formik.form.setFieldValue(name, currentValue);
      } else {
        dispatch(setErrorFields(error));
      }
    });
  }
}, [store.customer, name, dispatch, formik.field.value]);


  return (
    <InputProvider
      formik={formik}
      name={name}
      dispatch={dispatch}
      store={store}
      apiService={apiService}
      onChange={onChange}
      disableAutoSave={disableAutoSave}
    >
      {(args) => {
        const {
          hasError,
          errorMessage,
          handleOnChange,
          handleOnBlur
        } = args as InputProviderRenderProps;

        const safeErrorMessage =
          typeof errorMessage === 'string' || errorMessage === undefined
            ? errorMessage
            : undefined;

        const handleBlur = (
          event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
        ) => {
          setIsTouch(true);
          if (isDirty) {
            handleOnBlur(event);
            setFieldDirty(false);
          }
        };

        const handleChange = (
          event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        ) => {
          setFieldDirty(true);
          handleOnChange(event);
        };

        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              width: '100%',
              paddingTop: '4px',
              mb: '16px'
            }}
            style={customStyles}
          >
            <>
              <TextField
                variant="outlined"
                fullWidth
                type={type}
                name={name}
                label={label ?? ''}
                required={Boolean(required)}
                error={(hasError && isTouch) || Boolean(hasGroupError)}
                onBlur={handleBlur}
                value={(formik.field.value as string) ?? ''}
                placeholder={placeholder}
                onChange={handleChange}
              />

              <ErrorFieldCheck
                hasError={hasError}
                isTouch={isTouch}
                errorMessage={safeErrorMessage}
                setIsTouch={setIsTouch}
              />
            </>

            {footer && (
              <Box
                sx={{
                  mt: '3px',
                  fontSize: '12px',
                  fontWeight: 300,
                  '& a': { color: 'var(--cargill-green)' }
                }}
              >
                {footer}
              </Box>
            )}
          </Box>
        );
      }}
    </InputProvider>
  );
}

export default FieldInput;
